import { Injectable } from '@angular/core';
import { AuthService } from '../../auth/services/auth.service';
import { UserRole } from '../../auth/models/user.model';

export interface BadgeItem {
    type: string;
    value: string;
}
export interface Saperator {
    name: string;
    type?: string;
}
export interface SubChildren {
    state: string;
    name: string;
    type?: string;
}
export interface ChildrenItems {
    state: string;
    name: string;
    type?: string;
    child?: SubChildren[];
}

export interface Menu {
    state: string;
    name: string;
    type: string; // 'link' | 'saperator' | 'subheader' (a label inside a section) | ...
    icon: string;
    badge?: BadgeItem[];
    saperator?: Saperator[];
    children?: ChildrenItems[];
    roles?: string[]; // Roles that can access this menu item
    privilegeModules?: string[]; // Privilege modules that grant access
    privileges?: string[]; // Exact privilege names that grant access (any one match)
    exact?: boolean;
    danger?: boolean; // Renders the entry in the alert (red) colour
}

/**
 * A separator and the links under it, for the collapsible vertical sidebar.
 * Links listed before the first separator form a section with an empty name,
 * which the sidebar renders without a header and never collapses.
 */
export interface SidebarSection {
    name: string;
    icon: string;
    items: Menu[];
}

// The sidebar is an accordion: the first section listed here starts expanded,
// and reopens whenever the user collapses the open one.
const SECTIONS_OPEN_BY_DEFAULT = ['Vouchers'];

/**
 * Entry shape for the horizontal top-nav.
 * Discriminated union — template uses `*ngIf="entry.type === ..."` to narrow.
 */
export type HorizontalMenuEntry =
    | { type: 'link'; name: string; icon: string; state: string; exact: boolean }
    | { type: 'group'; name: string; icon: string; children: Menu[] };

// Separators carry no role or privilege gate on purpose: a separator the
// filter removed would hand its links to the previous section. Sections left
// with no visible links are dropped by getSidebarSections() and
// getHorizontalMenu() instead.
const MENUITEMS = [
    {
        // Home page for every user: listed before the first separator, so the
        // sidebar shows it on its own above the sections.
        state: 'dms/dashboard',
        name: 'Dashboard',
        type: 'link',
        icon: 'dashboard'
    },
    {
        // Also before the first separator, so it sits under Dashboard with no
        // section header.
        state: 'dms/fraud-dashboard',
        name: 'Anomaly Alerts',
        type: 'link',
        icon: 'gpp_bad',
        danger: true,
        // The exact privilege the alert APIs check. The 'verification' module
        // would also admit plain verifiers, who then hit a 403 on every panel.
        // Which vouchers appear is narrowed by the role's category permissions
        // on the API side.
        privileges: ['verification.view_alerts']
    },
    {
        state: '',
        name: 'Vouchers',
        type: 'saperator',
        icon: 'folder'
    },
    {
        state: 'dms/documents',
        name: 'All Vouchers',
        type: 'link',
        icon: 'description',
        privilegeModules: ['document']
    },
    {
        state: 'dms/search',
        name: 'Search Vouchers',
        type: 'link',
        icon: 'search',
        privilegeModules: ['search']
    },
    // {
    //     state: 'dms/search-identities',
    //     name: 'Search Identities',
    //     type: 'link',
    //     icon: 'person_search',
    //     privilegeModules: ['search_identities']
    // },
    // Upload Voucher (dms/upload) has no sidebar link: the Dashboard and the
    // Vouchers page both show an Upload Voucher button to holders of
    // document.create.
    {
        state: 'dms/verify',
        name: 'Verify Voucher',
        type: 'link',
        icon: 'verified_user',
        privilegeModules: ['verification']
    },
    {
        state: 'dms/workflow/documents',
        name: 'Voucher Approvals',
        type: 'link',
        icon: 'fact_check',
        // workflow.view alone is enough to read the queue; acting on a document
        // additionally needs workflow.approve, which the queue checks per row.
        privileges: ['workflow.view', 'workflow.approve']
    },
    {
        state: 'dms/bulk-upload',
        name: 'Bulk Upload',
        type: 'link',
        icon: 'backup',
        privilegeModules: ['bulk_upload']
    },
    // {
    //     state: 'dms/docgen',
    //     name: 'Document Generation',
    //     type: 'link',
    //     icon: 'note_add'
    // },
    // {
    //     state: 'dms/data-quality',
    //     name: 'Document & Data Quality',
    //     type: 'link',
    //     icon: 'assessment'
    // },
    // {
    //     state: '',
    //     name: 'Risk & Compliance',
    //     type: 'saperator',
    //     icon: 'shield'
    // },
    // {
    //     // Reconcile Bank Statements, Internal Audit and Research Grant
    //     // Management as tabs; any one module's privilege opens it and the page
    //     // shows only the tabs the user can use. The badge is the open
    //     // high-severity alert count across reconciliation and grants.
    //     state: 'dms/compliance-console',
    //     name: 'Compliance Console',
    //     type: 'link',
    //     icon: 'verified_user',
    //     privilegeModules: ['reconciliation', 'internal_audit', 'research_grant']
    // },
    {
        state: '',
        name: 'Assets',
        type: 'saperator',
        icon: 'inventory_2'
    },
    {
        state: 'dms/assets/dashboard',
        name: 'Overview',
        type: 'link',
        icon: 'dashboard_customize',
        privilegeModules: ['asset']
    },
    {
        state: 'dms/assets',
        name: 'Asset Register',
        type: 'link',
        icon: 'inventory_2',
        privilegeModules: ['asset'],
        exact: true
    },
    {
        state: 'dms/assets/assignments',
        name: 'Assignments',
        type: 'link',
        icon: 'assignment_ind',
        privilegeModules: ['asset']
    },
    {
        state: 'dms/assets/transfers',
        name: 'Transfers',
        type: 'link',
        icon: 'swap_horiz',
        privilegeModules: ['asset']
    },
    {
        state: 'dms/assets/verification',
        name: 'Asset Verification',
        type: 'link',
        icon: 'fact_check',
        privileges: ['asset.verify']
    },
    {
        state: 'dms/assets/depreciation',
        name: 'Depreciation Run',
        type: 'link',
        icon: 'trending_down',
        privileges: ['asset.depreciation']
    },
    {
        state: 'dms/assets/tracker',
        name: 'Asset Tracker',
        type: 'link',
        icon: 'location_on',
        privilegeModules: ['asset']
    },
    {
        state: 'dms/assets/reports',
        name: 'Reports',
        type: 'link',
        icon: 'assessment',
        privilegeModules: ['asset']
    },
    {
        state: '',
        name: 'Budget',
        type: 'saperator',
        icon: 'payments'
    },
    {
        // Named for the register this screen holds: budget.view is accepted
        // too, and the upload form inside it is hidden without budget.create.
        // A reader therefore reaches the register; only an uploader sees the form.
        state: 'dms/budget/upload',
        name: 'Budget Register',
        type: 'link',
        icon: 'account_balance_wallet',
        privileges: ['budget.create', 'budget.view']
    },
    {
        // Registered after Budget Register: the module reads in the order the
        // work happens -- file the sanction order, then track it.
        //
        // Gated on the exact privileges rather than the 'budget' module: every
        // budget.* privilege resolves to that one module, so a module check
        // would show Tracking to someone holding only budget.create and
        // the Register to someone holding only budget.track.
        state: 'dms/budget/tracking',
        name: 'Budget Tracking',
        type: 'link',
        icon: 'query_stats',
        privileges: ['budget.track', 'budget.view']
    },
    {
        state: '',
        name: 'Administration',
        type: 'saperator',
        icon: 'settings'
    },
    {
        state: '',
        name: 'Access',
        type: 'subheader',
        icon: ''
    },
    {
        state: 'dms/users',
        name: 'Users',
        type: 'link',
        icon: 'people',
        roles: ['admin'],
        privilegeModules: ['user']
    },
    {
        state: 'dms/roles',
        name: 'Roles',
        type: 'link',
        icon: 'badge',
        roles: ['admin'],
        privilegeModules: ['role']
    },
    {
        state: 'dms/privileges',
        name: 'Privileges',
        type: 'link',
        icon: 'security',
        roles: ['admin'],
        privilegeModules: ['privilege']
    },
    {
        state: '',
        name: 'Configuration',
        type: 'subheader',
        icon: ''
    },
    {
        state: 'dms/document-config',
        name: 'Voucher Configuration',
        type: 'link',
        icon: 'settings_applications',
        roles: ['admin'],
        privilegeModules: ['docconfig']
    },
    {
        // Also reachable from a button in the Voucher Approvals header,
        // shown for the same privilege.
        state: 'dms/workflow/configuration',
        name: 'Workflow Configuration',
        type: 'link',
        icon: 'account_tree',
        privileges: ['workflow.configure']
    },
    {
        // Admin only: no privilege module opens it, since any system.*
        // privilege would otherwise reveal it.
        state: 'dms/rule-engine',
        name: 'Rule Engine',
        type: 'link',
        icon: 'rule',
        roles: ['admin']
    },
    {
        state: 'dms/search-configurations',
        name: 'Search Settings',
        type: 'link',
        icon: 'tune',
        privilegeModules: ['search_config']
    },
    {
        state: 'dms/blockchain-config',
        name: 'Blockchain',
        type: 'link',
        icon: 'link',
        roles: ['admin'],
        privilegeModules: ['blockchain']
    },
    {
        // Admin, or a role granted the exact system.settings privilege. Not the
        // 'system' module: that would also reveal it to system.audit_logs.
        state: 'dms/configuration',
        name: 'System Settings',
        type: 'link',
        icon: 'settings',
        roles: ['admin'],
        privileges: ['system.settings']
    },
    {
        state: '',
        name: 'Monitoring',
        type: 'subheader',
        icon: ''
    },
    {
        state: 'dms/audit-log',
        name: 'Audit Log',
        type: 'link',
        icon: 'history',
        roles: ['admin'],
        privilegeModules: ['system']
    }
];

/** Sidebar route of the Compliance Assurance Console; its alert badges sit on it. */
export const COMPLIANCE_CONSOLE_STATE = 'dms/compliance-console';

const badgeCounts = new Map<string, Map<string, number>>();

/**
 * Set one source's alert count on a sidebar link; the badge shows the sum of
 * every source's count on that link, and clears at zero. The sidebar renders
 * MENUITEMS' own objects, so the change shows on the next change detection.
 */
export function setMenuBadgeCount(state: string, source: string, count: number): void {
    const counts = badgeCounts.get(state) ?? new Map<string, number>();
    counts.set(source, count);
    badgeCounts.set(state, counts);
    const total = [...counts.values()].reduce((a, b) => a + b, 0);
    const item = (MENUITEMS as Menu[]).find((m) => m.state === state);
    if (item) item.badge = total ? [{ type: 'danger', value: String(total) }] : [];
}

/**
 * Drop each `subheader` that has no link under it before the next
 * sub-heading, as happens when the privilege filter removes all its links.
 */
function withoutEmptySubheaders(items: Menu[]): Menu[] {
    return items.filter((item, i) => {
        if (item.type !== 'subheader') return true;
        const next = items.slice(i + 1).find(m => m.type === 'subheader' || m.type === 'link');
        return next?.type === 'link';
    });
}

@Injectable()
export class MenuItems {
    constructor(private authService: AuthService) {}

    getMenuitem(): Menu[] {
        // Admin sees all menu items. Admin is the API's isAdmin flag, not the
        // role name (V-API-01).
        // if (userRole === 'admin') {
        if (this.authService.isAdmin()) {
            return MENUITEMS;
        }

        return MENUITEMS.filter(item => {
            const hasRoles = item.roles && item.roles.length > 0;
            const hasPrivilegeModules = item.privilegeModules && item.privilegeModules.length > 0;
            const hasPrivileges = item.privileges && item.privileges.length > 0;

            // No restrictions — visible to all users
            if (!hasRoles && !hasPrivilegeModules && !hasPrivileges) {
                return true;
            }

            // Check if user's role matches. hasAnyRole, not a name compare: 'admin'
            // is the API's isAdmin flag, never a role that happens to be named so.
            if (hasRoles && this.authService.hasAnyRole(item.roles as UserRole[])) {
                return true;
            }

            // Check if user has matching privilege modules
            if (hasPrivilegeModules && this.authService.hasPrivilegeModule(item.privilegeModules!)) {
                return true;
            }

            // Check for an exact privilege. Needed wherever module granularity is
            // too coarse: modules are derived by splitting a privilege name on its
            // first dot, so `document.create` (upload) and `document.view`
            // (read-only) both resolve to the `document` module. Only the
            // privilege name itself distinguishes an uploader from a viewer.
            if (hasPrivileges && item.privileges!.some(p => this.authService.hasPrivilege(p))) {
                return true;
            }

            return false;
        });
    }

    /**
     * Vertical-sidebar variant of the menu: each `saperator` becomes a
     * collapsible section holding the links that follow it. Like
     * getHorizontalMenu(), call once and cache — it builds new arrays.
     * Sections left empty by the role/privilege filter are dropped.
     */
    getSidebarSections(): SidebarSection[] {
        // Links before the first separator (Dashboard) collect in a nameless
        // section that the sidebar shows without a header.
        let current: SidebarSection = { name: '', icon: '', items: [] };
        const sections: SidebarSection[] = [current];

        for (const item of this.getMenuitem()) {
            if (item.type === 'saperator') {
                current = { name: item.name, icon: item.icon, items: [] };
                sections.push(current);
            } else {
                current.items.push(item);
            }
        }

        return sections
            .map(section => ({ ...section, items: withoutEmptySubheaders(section.items) }))
            .filter(section => section.items.length > 0);
    }

    isSectionOpenByDefault(name: string): boolean {
        return SECTIONS_OPEN_BY_DEFAULT.includes(name);
    }

    /**
     * Horizontal-nav variant of the menu.
     *
     * IMPORTANT: callers should invoke this ONCE (e.g. in ngOnInit) and
     * cache the result. Do NOT bind it directly in a template `*ngFor` —
     * it builds a new array on every call, which would make Angular
     * rebuild the nav (and re-subscribe routerLinkActive) on every
     * change-detection cycle.
     *
     * Transformation applied on top of the role/privilege-filtered list:
     *  - Links before the first `saperator` (Dashboard) become standalone
     *    top links.
     *  - Groups each run of links under its preceding `saperator`
     *    (Vouchers / Assets / Administration ...), producing a hover
     *    dropdown per group.
     *  - Drops sub-headings with no visible link under them, then empty
     *    groups, so inaccessible sections don't show up as dead dropdowns.
     */
    getHorizontalMenu(): HorizontalMenuEntry[] {
        const flat = this.getMenuitem();
        const result: HorizontalMenuEntry[] = [];
        let currentGroupName: string | null = null;
        let currentGroupIcon: string | null = null;
        let currentGroupChildren: Menu[] = [];

        const flushCurrentGroup = () => {
            const children = withoutEmptySubheaders(currentGroupChildren);
            if (currentGroupName !== null && children.length > 0) {
                result.push({
                    type: 'group',
                    name: currentGroupName,
                    icon: currentGroupIcon || 'folder',
                    children
                });
            }
        };

        for (const item of flat) {
            if (item.type === 'saperator') {
                flushCurrentGroup();
                currentGroupName = item.name;
                currentGroupIcon = item.icon;
                currentGroupChildren = [];
                continue;
            }

            // Links ahead of every separator are top-level links.
            if (currentGroupName === null && item.type === 'link') {
                result.push({
                    type: 'link',
                    name: item.name,
                    icon: item.icon,
                    state: item.state,
                    exact: true
                });
                continue;
            }

            if (currentGroupName !== null) {
                currentGroupChildren.push(item);
            }
        }

        flushCurrentGroup();
        return result;
    }
}
